import { ApiError, getEventTariff, upsertEventTariff } from '@jq4race/api-client';
import { LocalizedError, useTranslation } from '@jq4race/i18n';
import type { EventTariffResponse, UpsertEventTariffRequest } from '@jq4race/shared';
import { CheckCircle2, TriangleAlert, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import type { EventTariffFormValues } from './EventTariffForm';
import { EventTariffForm } from './EventTariffForm';

// Admin UI section for the event-specific tariff override (event-tariff-override,
// design §5). Embedded as a tab in EditEventPage — only meaningful for
// `per_attendee` events (the parent gates tab visibility on chargeMode).
// Mirrors location-tariffs/ui/pages/TariffPage.tsx, minus the page chrome
// (PageHeader lives on EditEventPage already) and minus nightly support.

const FEEDBACK_TIMEOUT_MS = 5000;

interface EventTariffSectionProps {
  eventId: string;
}

// ── Cents ↔ Dollars conversion helpers (form boundary only) ───────────────

/** Convert integer cents to a dollars number for display. Null-safe. */
function centsToDisplay(cents: number | null | undefined): number | null {
  if (cents === null || cents === undefined) return null;
  return cents / 100;
}

/** Convert API response to EventTariffFormValues (dollars for monetary fields). */
function responseToFormValues(tariff: EventTariffResponse): Partial<EventTariffFormValues> {
  return {
    mode: tariff.mode as 'free' | 'flat' | 'hourly',
    differentiatedByDay: tariff.differentiatedByDay,
    amountCents: centsToDisplay(tariff.amountCents),
    dailyCapCents: centsToDisplay(tariff.dailyCapCents),
    weekdayAmountCents: centsToDisplay(tariff.weekdayAmountCents),
    weekendAmountCents: centsToDisplay(tariff.weekendAmountCents),
  };
}

// ── Component ─────────────────────────────────────────────────────────────

export function EventTariffSection({ eventId }: EventTariffSectionProps) {
  const { t } = useTranslation('events');
  const [initialValues, setInitialValues] = useState<Partial<EventTariffFormValues> | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const tariff = await getEventTariff(eventId);
      setInitialValues(responseToFormValues(tariff));
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        // No tariff configured yet — show an empty form.
        setInitialValues(undefined);
      } else {
        setError(translateApiError(e, t, { fallback: t('eventTariffSection.loadError') }));
      }
    } finally {
      setLoading(false);
    }
  }, [eventId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!success && !error) return;
    const id = window.setTimeout(() => {
      setSuccess(null);
      setError(null);
    }, FEEDBACK_TIMEOUT_MS);
    return () => window.clearTimeout(id);
  }, [success, error]);

  async function handleSubmit(values: UpsertEventTariffRequest) {
    setError(null);
    try {
      const saved = await upsertEventTariff(eventId, values);
      setInitialValues(responseToFormValues(saved));
      setSuccess(t('eventTariffSection.saveSuccess'));
    } catch (e) {
      throw e instanceof ApiError ? new LocalizedError(translateApiError(e, t)) : e;
    }
  }

  return (
    <div className="space-y-6">
      <FeedbackBanner tone="error" message={error} onDismiss={() => setError(null)} />
      <FeedbackBanner tone="success" message={success} onDismiss={() => setSuccess(null)} />

      <div className="rounded-sm border border-jq-sand/70 bg-jq-surface-0 p-4 sm:p-6">
        {loading ? (
          <p className="text-[13px] text-jq-graphite">{t('eventTariffSection.loading')}</p>
        ) : (
          <EventTariffForm
            onSubmit={handleSubmit}
            defaultValues={initialValues}
            submitLabel={t('eventTariffForm.saveTariff')}
          />
        )}
      </div>
    </div>
  );
}

// ── Feedback banner ────────────────────────────────────────────────────────

interface FeedbackBannerProps {
  tone: 'error' | 'success';
  message: string | null;
  onDismiss: () => void;
}

function FeedbackBanner({ tone, message, onDismiss }: FeedbackBannerProps) {
  const { t } = useTranslation('events');
  if (!message) return null;
  const Icon = tone === 'success' ? CheckCircle2 : TriangleAlert;
  const palette =
    tone === 'success'
      ? 'border-jq-success/40 bg-jq-success/10 text-jq-success'
      : 'border-jq-danger/40 bg-jq-danger/10 text-jq-danger';
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-sm border px-4 py-3 text-[13px] ${palette}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('feedback.dismiss')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
